/**
 * FocusManager — Spatial focus navigation for HTML/CSS panels.
 *
 * Manages a stack of focus groups. Each group corresponds to an open panel
 * and contains the set of [data-focusable] elements within it.
 * Navigation is constrained to the topmost group on the stack.
 *
 * Works standalone without InputManager for testing.
 *
 * @module FocusManager
 */

/**
 * @typedef {Object} FocusGroup
 * @property {string} name
 * @property {HTMLElement} container
 * @property {HTMLElement[]} elements
 * @property {HTMLElement|null} lastFocused — element that was focused when this group was pushed onto stack
 */

/** CSS class applied to the currently focused element. */
const FOCUS_CLASS = 'focus-active';

/**
 * Perpendicular offset penalty multiplier for spatial scoring.
 * Higher values penalize elements that are far off-axis.
 */
const PERPENDICULAR_PENALTY = 2.5;

class FocusManager {
  constructor() {
    /** @type {FocusGroup[]} */
    this._groupStack = [];

    /** @type {HTMLElement|null} */
    this._currentFocus = null;

    /** Callback when cancel is triggered on an empty stack. @type {Function|null} */
    this.onEmptyCancel = null;
  }

  // ──────────────────────────────────────────
  // Group management
  // ──────────────────────────────────────────

  /**
   * Push a new focus group onto the stack.
   * Scans the container for [data-focusable] elements.
   * The first focusable element receives focus.
   *
   * @param {string} name — group name (usually the panel id)
   * @param {HTMLElement} containerEl
   */
  pushGroup(name, containerEl) {
    const elements = this._scanFocusable(containerEl);

    /** @type {FocusGroup} */
    const group = {
      name,
      container: containerEl,
      elements,
      lastFocused: this._currentFocus,
    };

    this._groupStack.push(group);

    // Focus the first element in the new group
    if (elements.length > 0) {
      this.setFocus(elements[0]);
    } else {
      this._clearFocus();
    }
  }

  /**
   * Pop the topmost focus group and restore the previous group's focus.
   * @returns {FocusGroup|undefined} the popped group
   */
  popGroup() {
    const popped = this._groupStack.pop();
    if (!popped) return undefined;

    this._clearFocus();

    // Restore focus from previous group
    const top = this._currentGroup();
    if (top && top.lastFocused) {
      this.setFocus(top.lastFocused);
    } else if (top && top.elements.length > 0) {
      this.setFocus(top.elements[0]);
    }

    return popped;
  }

  /**
   * Get the topmost focus group.
   * @returns {FocusGroup|null}
   */
  _currentGroup() {
    return this._groupStack.length > 0
      ? this._groupStack[this._groupStack.length - 1]
      : null;
  }

  /**
   * Rescan the current group's container for focusable elements.
   * Useful when panel content changes dynamically.
   */
  refreshGroup() {
    const group = this._currentGroup();
    if (!group) return;
    group.elements = this._scanFocusable(group.container);

    // If current focus is no longer in the group, focus the first element
    if (this._currentFocus && !group.elements.includes(this._currentFocus)) {
      if (group.elements.length > 0) {
        this.setFocus(group.elements[0]);
      } else {
        this._clearFocus();
      }
    }
  }

  /**
   * Scan a container for [data-focusable] elements.
   * Filters out elements that are not visible (hidden by CSS display:none or similar).
   * @param {HTMLElement} container
   * @returns {HTMLElement[]}
   */
  _scanFocusable(container) {
    const all = Array.from(container.querySelectorAll('[data-focusable]'));
    return all.filter(el => {
      // offsetParent is null for elements with display:none or visibility:hidden ancestors
      // Exception: position:fixed elements also have null offsetParent, so check that
      if (el.offsetParent === null) {
        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden') return false;
        // Check if an ancestor is hidden
        let parent = el.parentElement;
        while (parent && parent !== container) {
          const pStyle = window.getComputedStyle(parent);
          if (pStyle.display === 'none' || pStyle.visibility === 'hidden') return false;
          parent = parent.parentElement;
        }
      }
      return true;
    });
  }

  // ──────────────────────────────────────────
  // Focus control
  // ──────────────────────────────────────────

  /**
   * Set focus to a specific element.
   * @param {HTMLElement} element
   */
  setFocus(element) {
    if (this._currentFocus === element) return;

    this._clearFocus();
    this._currentFocus = element;
    element.classList.add(FOCUS_CLASS);
    element.setAttribute('aria-current', 'true');

    // Scroll into view if needed
    if (element.scrollIntoViewIfNeeded) {
      element.scrollIntoViewIfNeeded(false);
    } else {
      element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  /**
   * Remove focus styling from the current element.
   */
  _clearFocus() {
    if (this._currentFocus) {
      this._currentFocus.classList.remove(FOCUS_CLASS);
      this._currentFocus.removeAttribute('aria-current');
      this._currentFocus = null;
    }
  }

  /**
   * Get the currently focused element.
   * @returns {HTMLElement|null}
   */
  getCurrentFocus() {
    return this._currentFocus;
  }

  // ──────────────────────────────────────────
  // Spatial navigation
  // ──────────────────────────────────────────

  /**
   * Move focus in a spatial direction within the current group.
   * @param {'up'|'down'|'left'|'right'} direction
   */
  navigate(direction) {
    const group = this._currentGroup();
    if (!group || group.elements.length === 0) return;

    // If nothing is focused, focus the first element
    if (!this._currentFocus || !group.elements.includes(this._currentFocus)) {
      this.setFocus(group.elements[0]);
      return;
    }

    const target = this._findBestCandidate(this._currentFocus, direction, group.elements);
    if (target) {
      this.setFocus(target);
    }
  }

  /**
   * Navigate linearly (Tab / Shift+Tab style).
   * @param {number} delta — +1 for forward, -1 for backward
   */
  navigateLinear(delta) {
    const group = this._currentGroup();
    if (!group || group.elements.length === 0) return;

    if (!this._currentFocus || !group.elements.includes(this._currentFocus)) {
      this.setFocus(group.elements[0]);
      return;
    }

    const idx = group.elements.indexOf(this._currentFocus);
    const nextIdx = (idx + delta + group.elements.length) % group.elements.length;
    this.setFocus(group.elements[nextIdx]);
  }

  /**
   * Find the best focusable candidate in a given direction from a source element.
   *
   * Scoring algorithm:
   * 1. Filter elements that are in the requested direction from the source center.
   * 2. Score each by: distance along movement axis + (perpendicular offset * penalty).
   * 3. Return the element with the lowest score.
   *
   * @param {HTMLElement} source
   * @param {'up'|'down'|'left'|'right'} direction
   * @param {HTMLElement[]} candidates
   * @returns {HTMLElement|null}
   */
  _findBestCandidate(source, direction, candidates) {
    const sourceRect = source.getBoundingClientRect();
    const srcCx = sourceRect.left + sourceRect.width / 2;
    const srcCy = sourceRect.top + sourceRect.height / 2;

    let bestScore = Infinity;
    let bestEl = null;

    for (const el of candidates) {
      if (el === source) continue;

      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;

      const dx = cx - srcCx;
      const dy = cy - srcCy;

      // Check that the element is in the correct direction
      let axisDistance;
      let perpDistance;

      switch (direction) {
        case 'up':
          if (dy >= 0) continue; // must be above
          axisDistance = Math.abs(dy);
          perpDistance = Math.abs(dx);
          break;
        case 'down':
          if (dy <= 0) continue; // must be below
          axisDistance = Math.abs(dy);
          perpDistance = Math.abs(dx);
          break;
        case 'left':
          if (dx >= 0) continue; // must be to the left
          axisDistance = Math.abs(dx);
          perpDistance = Math.abs(dy);
          break;
        case 'right':
          if (dx <= 0) continue; // must be to the right
          axisDistance = Math.abs(dx);
          perpDistance = Math.abs(dy);
          break;
        default:
          continue;
      }

      const score = axisDistance + perpDistance * PERPENDICULAR_PENALTY;

      if (score < bestScore) {
        bestScore = score;
        bestEl = el;
      }
    }

    return bestEl;
  }

  // ──────────────────────────────────────────
  // Actions
  // ──────────────────────────────────────────

  /**
   * Trigger a click on the currently focused element.
   */
  confirm() {
    if (this._currentFocus) {
      this._currentFocus.click();
    }
  }

  /**
   * Cancel the current group: pops it from the stack.
   * Panels should listen for their group being popped and hide themselves.
   */
  cancel() {
    if (this._groupStack.length > 0) {
      const group = this._currentGroup();
      this.popGroup();
      // Dispatch a custom event so the panel can react
      if (group && group.container) {
        group.container.dispatchEvent(new CustomEvent('focusgroup:cancelled', {
          bubbles: true,
          detail: { name: group.name },
        }));
      }
    } else if (this.onEmptyCancel) {
      this.onEmptyCancel();
    }
  }

  /**
   * Get the number of groups on the stack.
   * @returns {number}
   */
  getGroupCount() {
    return this._groupStack.length;
  }

  /**
   * Clear all groups and focus state. Used for cleanup/reset.
   */
  clear() {
    this._clearFocus();
    this._groupStack = [];
  }
}

/** Singleton instance. */
const focusManager = new FocusManager();
export default focusManager;
